/**
 * Tipos da API /bonificacoes. Valores em CENTAVOS, como a folha.
 */
import { formatCents } from "@/lib/format";

export type StatusBonificacao = "rascunho" | "fechada";
export type TipoCalculoBonificacao = "multiplo" | "fixo";

export interface ConfigBonificacao {
  nome: string;
  diasExperiencia: number;
  proporcional: boolean;
  diasPeriodo: number;
  /** Centavos; nulo = sem teto. */
  tetoSalario: number | null;
  comissionadosRecebem: boolean;
  mesesComissao: number;
}

export interface CompetenciaBonificacaoResumo {
  id: string;
  bonificacaoId: string;
  parcela: number;
  competencia: string;
  status: StatusBonificacao;
  fechadaEm: string | null;
  reabertaCount: number;
  totalLiquido: number;
  totalColaboradores: number;
}

export interface Bonificacao {
  id: string;
  descricao: string | null;
  tipoCalculo: TipoCalculoBonificacao;
  multiplicador: number | null;
  valorFixo: number | null;
  numeroParcelas: number;
  /** Pago antes de fechar o ano da meta; a final do ano desconta. */
  adiantamento: boolean;
  /** AAAA-MM-DD — 31/12 do ano da meta; no adiantamento, a data de corte. */
  dataBase: string;
  diasExperiencia: number;
  proporcional: boolean;
  diasPeriodo: number;
  tetoSalario: number | null;
  comissionadosRecebem: boolean;
  mesesComissao: number;
  createdAt: string;
  competencias: CompetenciaBonificacaoResumo[];
}

export interface BonificacaoItem {
  id: string;
  colaboradorId: string;
  nome: string;
  email: string;
  cpf: string | null;
  cnpj: string | null;
  tomadorNome: string | null;
  dataAdmissao: string | null;
  salarioFixo: number;
  salarioConsiderado: number;
  /** Nulo = recebe integral. */
  diasApurados: number | null;
  valorTotal: number;
  /** Só na final: adiantamentos do ano descontados do total. */
  adiantamentoDescontado: number;
  valorBruto: number;
  descontoEmprestimo: number;
  ajusteManual: number;
  ajusteMotivo: string | null;
  liquido: number;
  exclusao: string | null;
  exclusaoRotulo: string | null;
  exclusaoDetalhe: string | null;
  avisos: string | null;
}

export interface BloqueioBonificacao {
  codigo: "PARCELA_ANTERIOR_ABERTA" | "ADIANTAMENTO_ABERTO" | "SEM_ADMISSAO" | "ITEM_SEM_TOMADOR" | "SEM_ITENS";
  mensagem: string;
  colaboradores: { id: string; nome: string; detalhe?: string }[];
}

export interface CompetenciaBonificacaoDetalhe {
  competencia: {
    id: string;
    parcela: number;
    competencia: string;
    status: StatusBonificacao;
    fechadaEm: string | null;
    reabertaCount: number;
    observacao: string | null;
    bonificacao: Omit<Bonificacao, "competencias"> & {
      competencias: { id: string; parcela: number; competencia: string; status: StatusBonificacao }[];
    };
  };
  itens: BonificacaoItem[];
  totais: {
    valorBruto: number;
    descontoEmprestimo: number;
    ajusteManual: number;
    liquido: number;
    colaboradores: number;
    excluidos: number;
  };
  bloqueios: BloqueioBonificacao[];
}

/** Resumo das regras, para o RH saber o que a bonificação usa (ou vai copiar). */
export const descreverRegras = (r: Omit<ConfigBonificacao, "nome">) =>
  [
    r.diasExperiencia > 0 ? `${r.diasExperiencia} dias de experiência não contam` : "sem experiência",
    r.proporcional ? `proporcional sobre ${r.diasPeriodo} dias` : "sem proporcional",
    r.tetoSalario ? `teto de ${formatCents(r.tetoSalario)} de salário` : "sem teto",
    r.comissionadosRecebem ? "comissionados recebem" : `comissionados dos últimos ${r.mesesComissao} meses não recebem`,
  ].join(" · ");

/** "2x o salário fixo" / "R$ 5.000,00 para todos". */
export const descreverCalculo = (b: Pick<Bonificacao, "tipoCalculo" | "multiplicador" | "valorFixo">) =>
  b.tipoCalculo === "multiplo"
    ? `${String(b.multiplicador ?? 0).replace(".", ",")}x o salário fixo`
    : `${formatCents(b.valorFixo ?? 0)} para todos`;
